import { useState, useEffect, useCallback, useMemo } from 'react';

type StatusFilter = 'all' | 'active' | string;
const STATUS_FILTER_KEY = 'taskStatusFilter';
const RESET_STATUS_FILTER_EVENT = 'reset_task_status_filter';

/** Reset both an open task list and the saved filter used by a new viewer. */
export const resetTaskStatusFilter = (): void => {
  localStorage.setItem(STATUS_FILTER_KEY, 'all');
  window.dispatchEvent(new Event(RESET_STATUS_FILTER_EVENT));
};

const loadFilter = <T extends string>(key: string, fallback: T): T => (
  (localStorage.getItem(key) as T) || fallback
);

const useSyncedFilter = <T extends string>(key: string, fallback: T) => {
  const [filter, setFilter] = useState<T>(() => loadFilter(key, fallback));

  useEffect(() => { localStorage.setItem(key, filter); }, [key, filter]);

  return [filter, setFilter] as const;
};

interface UseTaskFiltersResult {
  statusFilter: StatusFilter;
  setStatusFilter: (filter: StatusFilter) => void;
  hasActiveFilters: boolean;
  clearFilters: () => void;
}

export const useTaskFilters = (): UseTaskFiltersResult => {
  const [statusFilter, setStatusFilter] = useSyncedFilter<StatusFilter>(STATUS_FILTER_KEY, 'all');
  const clearFilters = useCallback(() => { setStatusFilter('all'); }, [setStatusFilter]);

  useEffect(() => {
    window.addEventListener(RESET_STATUS_FILTER_EVENT, clearFilters);

    return () => window.removeEventListener(RESET_STATUS_FILTER_EVENT, clearFilters);
  }, [clearFilters]);

  return useMemo(() => ({
    statusFilter,
    setStatusFilter,
    hasActiveFilters: statusFilter !== 'all',
    clearFilters,
  }), [statusFilter, setStatusFilter, clearFilters]);
};
